(() => {
  const SOURCE_SELECTOR = '[data-ct-scroll-source="true"]';
  const SECTION_SELECTOR = '.shopify-section';
  const MOBILE_BREAKPOINT = 749;

  const SETTINGS = {
    activeSwitchThreshold: 0.01,
    storyRevealNextCardSwitchThreshold: 0.035,

    entryDelay: 0,

    driftFactor: 0.92,
    driftMinMobile: 34,
    driftMinDesktop: 46,
    driftMaxMobile: 94,
    driftMaxDesktop: 160,

    behindDriftStart: 0.08,
    behindDriftCurvePower: 4.2,
    behindSlowdownStart: 0.58,

    overlayBase: 0.8,
    overlayDepthStep: 0.08,
    overlayMax: 0.98,

    stackExtraHeightDesktop: 140,
    stackExtraHeightTablet: 10,
    stackExtraHeightMobile: 90,

    imageTextExtraHeightDesktop: 40,
    imageTextExtraHeightTablet: 160,
    imageTextExtraHeightMobile: -60,

    featuredCollectionMobileStageBuffer: 0,
    featuredCollectionMobileEndHold: 0,

    storyRevealExtraBufferDesktop: 12,
    storyRevealExtraBufferMobile: 8,
    storyRevealScrollStart: 0.18,
    storyRevealScrollEnd: 0.82,

    storyRevealPreEntryCarryDesktop: 0.38,
    storyRevealPreEntryCarryMobile: 0.34,
    storyRevealPreEntryStartViewport: 1.06,
    storyRevealPreEntryEndViewportDesktop: 0.3,
    storyRevealPreEntryEndViewportMobile: 0.27,

    storyRevealLeadEaseBlend: 0.18,
    storyRevealVisualCarryWeightDesktop: 0.62,
    storyRevealVisualCarryWeightMobile: 0.6,

    dualImageRevealExtraBufferMobile: 8,
    dualImageRevealMobileOverlap: 0.22,
    dualImageRevealSecondSplitPeekMobile: 34,
    dualImageRevealSplitBottomTrimMobile: 50,
    dualImageRevealBottomTrimDesktop: 130,
    dualImageRevealBottomTrimMobile: 90
  };

  let scenes = [];
  let pendingGroups = [];
  let builtGroupKeys = new Set();
  let resizeTimer = null;
  let renderFrame = null;
  let lastViewportWidth = window.innerWidth || document.documentElement.clientWidth;
  let lastViewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const revealedSectionIds = new Set();

  let debugOverlay = null;
  let debugRebuildCount = 0;
  let debugClearCount = 0;

  function getDebugEnabled() {
    try {
      const params = new URLSearchParams(window.location.search);

      if (params.get('ct_stack_debug') === '1') {
        window.localStorage.setItem('ctStackDebug', 'true');
        return true;
      }

      if (params.get('ct_stack_debug') === '0') {
        window.localStorage.removeItem('ctStackDebug');
        return false;
      }

      return window.localStorage.getItem('ctStackDebug') === 'true';
    } catch (error) {
      return window.location.search.indexOf('ct_stack_debug=1') !== -1;
    }
  }

  const CT_STACK_DEBUG = getDebugEnabled();
  window.__ctStackDebugEnabled = CT_STACK_DEBUG;

  function updateDebugOverlay(entry) {
    if (!CT_STACK_DEBUG) return;

    if (!debugOverlay) {
      debugOverlay = document.createElement('div');
      debugOverlay.setAttribute('data-ct-stack-debug-overlay', 'true');
      debugOverlay.style.cssText = [
        'position: fixed',
        'left: 10px',
        'bottom: 10px',
        'z-index: 2147483647',
        'max-width: calc(100vw - 20px)',
        'padding: 8px 10px',
        'border-radius: 8px',
        'background: rgba(0, 0, 0, 0.82)',
        'color: #ffffff',
        'font: 11px/1.35 monospace',
        'pointer-events: none',
        'white-space: pre-wrap'
      ].join(';');

      document.documentElement.appendChild(debugOverlay);
    }

    debugOverlay.textContent = [
      `CT STACK: ${entry.label}`,
      `rebuild: ${debugRebuildCount} | clear: ${debugClearCount}`,
      `scrollY: ${entry.scrollY}`,
      `inner: ${entry.innerWidth}x${entry.innerHeight}`,
      `visual: ${entry.visualViewportWidth || 'n/a'}x${entry.visualViewportHeight || 'n/a'}`,
      entry.reason ? `reason: ${entry.reason}` : ''
    ].filter(Boolean).join('\n');
  }

  function debugStack(label, data = {}) {
    if (!CT_STACK_DEBUG) return;

    const visualViewport = window.visualViewport;

    const entry = {
      label,
      time: Math.round(window.performance ? window.performance.now() : Date.now()),
      scrollY: Math.round(window.scrollY || window.pageYOffset || 0),
      innerWidth: window.innerWidth || document.documentElement.clientWidth,
      innerHeight: window.innerHeight || document.documentElement.clientHeight,
      visualViewportWidth: visualViewport ? Math.round(visualViewport.width) : null,
      visualViewportHeight: visualViewport ? Math.round(visualViewport.height) : null,
      scenesCount: scenes.length,
      pendingGroupsCount: pendingGroups.length,
      ...data
    };

    window.__ctStackDebug = window.__ctStackDebug || [];
    window.__ctStackDebug.push(entry);

    if (window.__ctStackDebug.length > 500) {
      window.__ctStackDebug.shift();
    }

    console.log(`[CT STACK] ${label}`, entry);
    updateDebugOverlay(entry);
  }

  window.__ctStackDebugPush = debugStack;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function mapLinear(value, start, end) {
    const range = Math.max(end - start, 0.0001);
    return clamp((value - start) / range, 0, 1);
  }

  function mapInverseLinear(value, start, end) {
    const range = Math.max(start - end, 0.0001);
    return clamp((start - value) / range, 0, 1);
  }

  function smoothstep(edge0, edge1, value) {
    if (edge0 === edge1) {
      return value >= edge1 ? 1 : 0;
    }

    const t = clamp((value - edge0) / (edge1 - edge0), 0, 1);
    return t * t * (3 - 2 * t);
  }

  function easeOutCubic(value) {
    return 1 - Math.pow(1 - value, 3);
  }

  function parsePositiveNumber(value, fallback) {
    const parsed = parseFloat(String(value || '').trim());

    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function parseColor(value) {
    if (!value) return null;

    const trimmed = String(value).trim();

    if (trimmed.startsWith('#')) {
      let hex = trimmed.slice(1);

      if (hex.length === 3) {
        hex = hex
          .split('')
          .map((char) => char + char)
          .join('');
      }

      if (hex.length !== 6) return null;

      const int = parseInt(hex, 16);

      return [
        (int >> 16) & 255,
        (int >> 8) & 255,
        int & 255
      ];
    }

    const rgbMatch = trimmed.match(/rgba?\(([^)]+)\)/i);

    if (!rgbMatch) return null;

    const parts = rgbMatch[1]
      .split(',')
      .map((part) => parseFloat(part.trim()));

    if (parts.length < 3) return null;

    return [parts[0], parts[1], parts[2]];
  }

  function mixColor(from, to, progress) {
    return [
      Math.round(from[0] + (to[0] - from[0]) * progress),
      Math.round(from[1] + (to[1] - from[1]) * progress),
      Math.round(from[2] + (to[2] - from[2]) * progress)
    ];
  }

  function toRgbString(color) {
    return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
  }

  function getHeaderOffset() {
    const candidates = [
      document.querySelector('sticky-header'),
      document.querySelector('#shopify-section-header'),
      document.querySelector('.header-wrapper')
    ].filter(Boolean);

    let maxHeight = 0;

    candidates.forEach((element) => {
      const rect = element.getBoundingClientRect();

      if (rect.height > maxHeight) {
        maxHeight = rect.height;
      }
    });

    return Math.round(maxHeight);
  }

  function getClosestSection(element) {
    return element.closest(SECTION_SELECTOR);
  }

  function getPreviousSection(element) {
    let prev = element.previousElementSibling;

    while (prev) {
      if (prev.matches(SECTION_SELECTOR)) {
        return prev;
      }

      prev = prev.previousElementSibling;
    }

    return null;
  }

  function getEligibleSources() {
    const wrappers = Array.from(document.querySelectorAll(SOURCE_SELECTOR));

    return wrappers
      .map((wrapper) => {
        const section = getClosestSection(wrapper);
        if (!section) return null;

        const desktopOnly = wrapper.getAttribute('data-ct-scroll-desktop-only') === 'true';

        if (desktopOnly && window.innerWidth <= MOBILE_BREAKPOINT) {
          return null;
        }

        return {
          wrapper,
          section,
          sectionId: wrapper.getAttribute('data-ct-scroll-section-id') || '',
          group: wrapper.getAttribute('data-ct-scroll-group') || 'default',
          type: wrapper.getAttribute('data-ct-scroll-source-type') || 'default',
          peekDesktop: parseInt(wrapper.getAttribute('data-ct-scroll-peek-desktop') || '140', 10),
          peekMobile: parseInt(wrapper.getAttribute('data-ct-scroll-peek-mobile') || '90', 10)
        };
      })
      .filter(Boolean);
  }

  function buildGroups(items) {
    const groups = [];
    let currentGroup = [];

    items.forEach((item) => {
      if (!currentGroup.length) {
        currentGroup.push(item);
        return;
      }

      const last = currentGroup[currentGroup.length - 1];
      const previousSection = getPreviousSection(item.section);

      if (previousSection === last.section && item.group === last.group) {
        currentGroup.push(item);
      } else {
        groups.push(currentGroup);
        currentGroup = [item];
      }
    });

    if (currentGroup.length) {
      groups.push(currentGroup);
    }

    return groups.filter((group) => group.length > 1);
  }

  function sanitiseClone(root) {
    if (root.matches('[data-ct-story-reveal]')) {
      root.removeAttribute('data-ct-story-reveal');
      root.setAttribute('data-ct-story-reveal-clone', 'true');
    }

    root.querySelectorAll('[data-ct-story-reveal]').forEach((el) => {
      el.removeAttribute('data-ct-story-reveal');
      el.setAttribute('data-ct-story-reveal-clone', 'true');
    });

    root.querySelectorAll('[id]').forEach((el) => {
      el.removeAttribute('id');
    });

    root.querySelectorAll('.scroll-trigger').forEach((el) => {
      el.classList.remove('scroll-trigger');
    });

    root.querySelectorAll('.animate--fade-in').forEach((el) => {
      el.classList.remove('animate--fade-in');
    });

    root.querySelectorAll('.animate--slide-in').forEach((el) => {
      el.classList.remove('animate--slide-in');
    });

    root.querySelectorAll('.ct-reveal').forEach((el) => {
      el.classList.remove('is-in-view');
      el.classList.remove('ct-reveal-ready');
      el.setAttribute('data-ct-reveal-managed', 'stack');

      if (el.dataset.ctRevealSource) {
        el.innerHTML = el.dataset.ctRevealSource;
      }
    });

    root.querySelectorAll('*').forEach((el) => {
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
      el.style.removeProperty('visibility');
      el.style.removeProperty('clip-path');
      el.style.removeProperty('filter');
    });

    root.style.removeProperty('opacity');
    root.style.removeProperty('transform');
    root.style.removeProperty('visibility');
    root.style.removeProperty('clip-path');
    root.style.removeProperty('filter');

    if (root.classList.contains('ct-scroll-source-surface--story-reveal')) {
      root.style.removeProperty('background-color');
      root.style.removeProperty('--ct-intro-reveal');
      root.style.removeProperty('--ct-date-reveal');
      root.style.removeProperty('--ct-title-reveal');
      root.style.removeProperty('--ct-copy-reveal');
      root.style.removeProperty('--ct-media-opacity');
      root.style.removeProperty('--ct-media-y');
      root.style.removeProperty('--ct-media-scale');
      root.style.removeProperty('--ct-media-darkness');
      root.style.removeProperty('--ct-story-stack-shift');
    }

    if (root.classList.contains('ct-scroll-source-surface--dual-image-reveal')) {
      root.style.removeProperty('--ct-dual-stack-shift');
    }

    return root;
  }

  function isSectionAlreadyRevealed(sectionId) {
    return Boolean(sectionId) && revealedSectionIds.has(sectionId);
  }

  function markSectionAsRevealed(sectionId) {
    if (!sectionId) return;
    revealedSectionIds.add(sectionId);
  }

  function applyPersistedRevealState(entry) {
    if (!entry || !entry.card) return;

    if (window.ctRefreshReveal) {
      window.ctRefreshReveal(entry.card);
    }

    const revealElements = Array.from(entry.card.querySelectorAll('.ct-reveal'));

    revealElements.forEach((el) => {
      el.classList.add('ct-reveal-ready');
      el.classList.add('is-in-view');
    });

    entry.hasRevealed = true;
    entry.revealQueued = false;
  }

  function clearScenes() {
    debugClearCount += 1;

    debugStack('clearScenes', {
      clearCount: debugClearCount,
      existingScenes: document.querySelectorAll('.ct-scroll-scene').length,
      renderedWrappers: document.querySelectorAll('.ct-scroll-source-wrapper.is-rendered-in-scene').length
    });

    document.querySelectorAll('.ct-scroll-scene').forEach((scene) => {
      scene.remove();
    });

    document.querySelectorAll('.ct-scroll-source-wrapper.is-rendered-in-scene').forEach((wrapper) => {
      wrapper.classList.remove('is-rendered-in-scene');
    });

    scenes = [];
    pendingGroups = [];
    builtGroupKeys = new Set();
  }

  function getPeek(item, isMobile) {
    return isMobile ? item.peekMobile : item.peekDesktop;
  }

  function getEntryPeek(entry, isMobile) {
    if (
      isMobile &&
      entry &&
      entry.card &&
      entry.card.querySelector('[data-ct-dual-split-card][data-ct-dual-split-index="1"]')
    ) {
      return SETTINGS.dualImageRevealSecondSplitPeekMobile;
    }

    return getPeek(entry.item, isMobile);
  }

  function getEntryProgress(rawProgress) {
    return clamp(
      (rawProgress - SETTINGS.entryDelay) / (1 - SETTINGS.entryDelay),
      0,
      1
    );
  }

  function getBehindState(index, activeIndex, localProgress, sceneData, isMobile) {
    if (index >= activeIndex) {
      return {
        drift: 0,
        overlay: 0
      };
    }

    const controllerIndex = index + 1;
    const controllerProgress = clamp(localProgress[controllerIndex] || 0, 0, 1);
    const depth = activeIndex - index;

    const minVisible = isMobile ? SETTINGS.driftMinMobile : SETTINGS.driftMinDesktop;
    const maxDrift = Math.max(sceneData.stageHeight - minVisible, 0) * 0.5;

    const maxOverlay = clamp(
      SETTINGS.overlayBase + (depth - 1) * SETTINGS.overlayDepthStep,
      SETTINGS.overlayBase,
      SETTINGS.overlayMax
    );

    const overlayProgress = smoothstep(
      SETTINGS.behindSlowdownStart,
      1,
      controllerProgress
    );

    return {
      drift: Math.round(controllerProgress * maxDrift),
      overlay: clamp(easeOutCubic(overlayProgress) * maxOverlay, 0, SETTINGS.overlayMax)
    };
  }

  function getTallestFeaturedCollectionHeight(group) {
    return group.reduce((max, item) => {
      if (item.type !== 'featured-collection') return max;

      const height = Math.ceil(item.wrapper.getBoundingClientRect().height);
      return Math.max(max, height);
    }, 0);
  }

  function groupHasFeaturedCollection(group) {
    return group.some((item) => item.type === 'featured-collection');
  }

  function groupHasImageText(group) {
    return group.some((item) => item.type === 'image-text');
  }

  function groupHasDualImageReveal(group) {
    return group.some((item) => item.type === 'dual-image-reveal');
  }

  function hydrateStoryRevealMetrics(entry, stageHeight, isMobile) {
    const root = entry.card.querySelector('.ct-scroll-source-surface--story-reveal');

    if (!root) {
      entry.storyReveal = null;
      return;
    }

    const scrollEl = root.querySelector('.ct-story-reveal__stack-scroll') || root;
    const computed = window.getComputedStyle(root);

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const configuredDesktopVh = parsePositiveNumber(
      computed.getPropertyValue('--ct-story-reveal-height-desktop'),
      160
    );

    const configuredMobileVh = parsePositiveNumber(
      computed.getPropertyValue('--ct-story-reveal-height-mobile'),
      130
    );

    const configuredVh = isMobile ? configuredMobileVh : configuredDesktopVh;
    const configuredHeight = Math.round((viewportHeight * configuredVh) / 100);
    const configuredOverflow = Math.max(configuredHeight - stageHeight, 0);

    const scrollHeight = Math.max(
      Math.ceil(scrollEl.scrollHeight || 0),
      Math.ceil(scrollEl.getBoundingClientRect().height || 0)
    );

    entry.storyReveal = {
      root,
      scrollEl,
      overflow: Math.max(scrollHeight - stageHeight, 0),
      configuredOverflow,
      baseColor: parseColor(computed.backgroundColor) || [232, 216, 195],
      targetColor: parseColor(computed.getPropertyValue('--ct-story-reveal-target')) || [53, 31, 23]
    };

    root.style.setProperty('--ct-story-stack-shift', '0px');
  }

  function getDualImageRevealDesktopVisualHeight(root, stageHeight) {
    if (!root) return stageHeight;

    const computed = window.getComputedStyle(root);
    const rawValue = String(
      computed.getPropertyValue('--ct-dual-stack-visual-height-desktop') || '100%'
    ).trim();

    const parsed = parseFloat(rawValue);
    const safeValue = Number.isFinite(parsed) && parsed > 0 ? parsed : 100;

    let visualHeight = stageHeight;

    if (rawValue.endsWith('vh')) {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      visualHeight = Math.round((viewportHeight * safeValue) / 100);
    } else {
      visualHeight = Math.round((stageHeight * safeValue) / 100);
    }

    return clamp(visualHeight, 120, stageHeight);
  }

  function hydrateDualImageRevealMetrics(entry, stageHeight, isMobile, topOffset) {
    const root = entry.card.querySelector('.ct-scroll-source-surface--dual-image-reveal');

    if (!root) {
      entry.dualImageReveal = null;
      return;
    }

    root.style.setProperty('--ct-dual-stack-shift', '0px');

    if (root.hasAttribute('data-ct-dual-split-card')) {
      entry.dualImageReveal = null;
      return;
    }

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isStackedDualLayout = viewportWidth < 990;
    const grid = root.querySelector('.ct-dual-bg-reveal__grid');

    const visualHeightDesktop = getDualImageRevealDesktopVisualHeight(root, stageHeight);

    let overflow = 0;

    if (isStackedDualLayout && grid) {
      const safeTopOffset = Number.isFinite(topOffset) ? topOffset : 0;
      const visibleStageHeight = Math.max(viewportHeight - safeTopOffset, 320);

      const contentHeight = Math.max(
        Math.ceil(grid.scrollHeight || 0),
        Math.ceil(grid.getBoundingClientRect().height || 0),
        Math.ceil(root.scrollHeight || 0)
      );

      overflow = Math.max(contentHeight - visibleStageHeight, 0);
    }

    entry.dualImageReveal = {
      root,
      grid,
      overflow,
      isStackedLayout: isStackedDualLayout,
      visualHeightDesktop
    };
  }

  function updateDualImageRevealCard(entry, progress) {
    if (!entry.dualImageReveal) return;

    const overflow = entry.dualImageReveal.overflow || 0;

    if (overflow <= 0) {
      entry.dualImageReveal.root.style.setProperty('--ct-dual-stack-shift', '0px');
      return;
    }

    const phase = smoothstep(0, 1, clamp(progress, 0, 1));
    const shift = -Math.round(phase * overflow);

    entry.dualImageReveal.root.style.setProperty('--ct-dual-stack-shift', `${shift}px`);
  }

  function getCardExtraDistance(entry, isMobile) {
    if (entry && entry.dualImageReveal && entry.dualImageReveal.isStackedLayout) {
      const overflow = entry.dualImageReveal.overflow || 0;

      if (overflow <= 0) return 0;

      return overflow + SETTINGS.dualImageRevealExtraBufferMobile;
    }

    if (!entry.storyReveal) return 0;

    const measuredOverflow = entry.storyReveal.overflow || 0;
    const configuredOverflow = isMobile
      ? entry.storyReveal.configuredOverflow || 0
      : 0;

    const overflow = isMobile
      ? Math.max(measuredOverflow, configuredOverflow)
      : measuredOverflow;

    if (overflow <= 0) return 0;

    const buffer = isMobile
      ? SETTINGS.storyRevealExtraBufferMobile
      : SETTINGS.storyRevealExtraBufferDesktop;

    return overflow + buffer;
  }

  function getCardVisualHeight(entry, stageHeight) {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

    if (
      isMobile &&
      entry &&
      entry.card &&
      entry.card.querySelector('[data-prodotti-manuali-carousel]')
    ) {
      return Math.max(stageHeight - 80, 320);
    }

    if (!entry || !entry.dualImageReveal) {
      return stageHeight;
    }

    if (entry.dualImageReveal.isStackedLayout) {
      return stageHeight;
    }

    return clamp(
      entry.dualImageReveal.visualHeightDesktop || stageHeight,
      120,
      stageHeight
    );
  }

  function getVisibleStageHeight(topOffset) {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const safeTopOffset = Number.isFinite(topOffset) ? topOffset : 0;

    return Math.max(viewportHeight - safeTopOffset, 320);
  }

  function getIncomingTravelDistance(cards, index, stageHeight, topOffset) {
    if (index <= 0) return stageHeight;

    const previousEntry = cards[index - 1];

    if (
      previousEntry &&
      previousEntry.dualImageReveal &&
      previousEntry.dualImageReveal.isStackedLayout
    ) {
      return getVisibleStageHeight(topOffset);
    }

    return getCardVisualHeight(previousEntry, stageHeight);
  }

  function createScene(group, topOffset) {
    const firstSection = group[0].section;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const isMobile = viewportWidth <= MOBILE_BREAKPOINT;
    const isTablet = viewportWidth > MOBILE_BREAKPOINT && viewportWidth < 990;

    let extraHeight = isMobile
      ? SETTINGS.stackExtraHeightMobile
      : isTablet
        ? SETTINGS.stackExtraHeightTablet
        : SETTINGS.stackExtraHeightDesktop;

    if (groupHasImageText(group)) {
      extraHeight += isMobile
        ? SETTINGS.imageTextExtraHeightMobile
        : isTablet
          ? SETTINGS.imageTextExtraHeightTablet
          : SETTINGS.imageTextExtraHeightDesktop;
    }

    const baseStageHeight = Math.max(window.innerHeight - topOffset + extraHeight, 320);
    let stageHeight = baseStageHeight;

    const hasFeaturedCollection = groupHasFeaturedCollection(group);
    const hasDualImageReveal = groupHasDualImageReveal(group);
    const splitDualRevealCards = viewportWidth < 990 && hasDualImageReveal;

    if (isMobile) {
      const tallestFeaturedCollectionHeight = getTallestFeaturedCollectionHeight(group);

      if (tallestFeaturedCollectionHeight > 0) {
        stageHeight = Math.max(
          stageHeight,
          tallestFeaturedCollectionHeight + SETTINGS.featuredCollectionMobileStageBuffer
        );
      }
    }

    const scene = document.createElement('div');
    scene.className = 'ct-scroll-scene';

    if (group[0] && group[0].type === 'story-reveal') {
      scene.classList.add('ct-scroll-scene--story-lead');
    }

    scene.style.setProperty('--ct-scroll-top', `${topOffset}px`);
    scene.style.setProperty('--ct-scroll-stage-height', `${stageHeight}px`);

    const bottomTrim = hasDualImageReveal
      ? splitDualRevealCards
        ? SETTINGS.dualImageRevealSplitBottomTrimMobile
        : isMobile
          ? SETTINGS.dualImageRevealBottomTrimMobile
          : !isTablet
            ? SETTINGS.dualImageRevealBottomTrimDesktop
            : 0
      : 0;

    scene.style.setProperty('--ct-scroll-scene-bottom-trim', `${bottomTrim}px`);

    const sticky = document.createElement('div');
    sticky.className = 'ct-scroll-scene__sticky';

    const stage = document.createElement('div');
    stage.className = 'ct-scroll-scene__stage';

    sticky.appendChild(stage);
    scene.appendChild(sticky);

    const cards = [];

    const appendCard = (surfaceClone, item) => {
      const clone = sanitiseClone(surfaceClone);
      const card = document.createElement('div');
      const cardIndex = cards.length;

      card.className = 'ct-scroll-scene__card';
      card.style.setProperty('--ct-card-z', `${cardIndex + 1}`);
      card.appendChild(clone);

      stage.appendChild(card);

      const entry = {
        card,
        item,
        hasRevealed: false,
        revealQueued: false,
        storyReveal: null,
        dualImageReveal: null,
        dualShiftProgress: 0
      };

      cards.push(entry);

      if (isSectionAlreadyRevealed(item.sectionId)) {
        applyPersistedRevealState(entry);
      }

      return entry;
    };

    group.forEach((item) => {
      const sourceSurface = item.wrapper.querySelector('.ct-scroll-source-surface');
      if (!sourceSurface) return;

      const shouldSplitDualReveal =
        splitDualRevealCards &&
        item.type === 'dual-image-reveal';

      if (shouldSplitDualReveal) {
        const sourceGrid = sourceSurface.querySelector('.ct-dual-bg-reveal__grid');
        const sourceItems = sourceGrid
          ? Array.from(sourceGrid.children).filter((child) => {
              return child.classList.contains('ct-dual-bg-reveal__item');
            })
          : [];

        if (sourceItems.length > 1) {
          sourceItems.forEach((sourceItem, sourceItemIndex) => {
            const splitSurface = sourceSurface.cloneNode(true);
            const splitGrid = splitSurface.querySelector('.ct-dual-bg-reveal__grid');

            if (!splitGrid) return;

            const splitItems = Array.from(splitGrid.children).filter((child) => {
              return child.classList.contains('ct-dual-bg-reveal__item');
            });

            splitItems.forEach((splitItem, splitItemIndex) => {
              if (splitItemIndex !== sourceItemIndex) {
                splitItem.remove();
              }
            });

            splitSurface.setAttribute('data-ct-dual-split-card', 'true');
            splitSurface.setAttribute('data-ct-dual-split-index', String(sourceItemIndex));
            splitSurface.classList.add('ct-dual-bg-reveal--stack-split-card');

            appendCard(splitSurface, item);
          });

          item.wrapper.classList.add('is-rendered-in-scene');
          return;
        }
      }

      appendCard(sourceSurface.cloneNode(true), item);
      item.wrapper.classList.add('is-rendered-in-scene');
    });

    if (cards.length < 2) {
      return null;
    }

    firstSection.parentNode.insertBefore(scene, firstSection);

    cards.forEach((entry) => {
      hydrateStoryRevealMetrics(entry, stageHeight, isMobile);
      hydrateDualImageRevealMetrics(entry, stageHeight, isMobile, topOffset);
    });

    const starts = [0];
    const segments = [0];

    const leadDistance = getCardExtraDistance(cards[0], isMobile);

    const hasMobileLeadStoryReveal =
      isMobile &&
      cards[0] &&
      cards[0].item &&
      cards[0].item.type === 'story-reveal';

    let accumulated = hasMobileLeadStoryReveal ? 0 : leadDistance;

    cards.forEach((entry, index) => {
      if (index === 0) return;

      const peek = getEntryPeek(entry, isMobile);
      const incomingTravel = getIncomingTravelDistance(cards, index, stageHeight, topOffset);
      const baseSegment = Math.max(incomingTravel - peek, 120);
      const extraDistance = getCardExtraDistance(entry, isMobile);

      starts[index] = accumulated;
      segments[index] = baseSegment + extraDistance;
      accumulated += segments[index];
    });

    const endHold = isMobile && hasFeaturedCollection
      ? SETTINGS.featuredCollectionMobileEndHold
      : 0;

    const sceneHeight = stageHeight + accumulated + endHold;

    scene.style.setProperty('--ct-scroll-scene-height', `${sceneHeight}px`);

    return {
      scene,
      cards,
      starts,
      segments,
      stageHeight,
      topOffset,
      scrollDistance: accumulated + endHold,
      leadDistance
    };
  }

  function queueCardReveal(entry) {
    if (entry.revealQueued || entry.hasRevealed) return;

    entry.revealQueued = true;

    const revealElements = Array.from(entry.card.querySelectorAll('.ct-reveal'));

    if (!revealElements.length) {
      entry.hasRevealed = true;
      markSectionAsRevealed(entry.item.sectionId);
      entry.revealQueued = false;
      return;
    }

    const refreshAndShow = () => {
      if (window.ctRefreshReveal) {
        window.ctRefreshReveal(entry.card);
      }

      revealElements.forEach((el) => {
        el.classList.add('ct-reveal-ready');
      });
    };

    requestAnimationFrame(() => {
      refreshAndShow();

      window.setTimeout(() => {
        refreshAndShow();

        requestAnimationFrame(() => {
          revealElements.forEach((el) => {
            el.classList.add('is-in-view');
          });

          entry.hasRevealed = true;
          markSectionAsRevealed(entry.item.sectionId);
          entry.revealQueued = false;
        });
      }, 80);
    });
  }

  function getLeadCardProgress(sceneData, entry, progress, firstCardTrigger) {
    const leadDistance = Math.max(sceneData.leadDistance || 0, 0);

    if (leadDistance <= 0) {
      return firstCardTrigger ? 1 : 0;
    }

    const raw = clamp(progress / leadDistance, 0, 1);

    if (!entry || !entry.storyReveal) {
      return raw;
    }

    const isLeadStoryReveal =
      entry.item &&
      entry.item.type === 'story-reveal';

    if (isLeadStoryReveal) {
      return raw;
    }

    const ramped = smoothstep(0, 1, raw);
    const eased = easeOutCubic(ramped);
    const blend = clamp(SETTINGS.storyRevealLeadEaseBlend, 0, 1);

    return clamp(ramped * (1 - blend) + eased * blend, 0, 1);
  }

  function getCardTimelineProgress(
    sceneData,
    entry,
    index,
    activeIndex,
    localProgress,
    progress,
    firstCardTrigger
  ) {
    const isLeadStoryReveal =
      index === 0 &&
      entry &&
      entry.item &&
      entry.item.type === 'story-reveal';

    if (isLeadStoryReveal) {
      return getLeadCardProgress(sceneData, entry, progress, firstCardTrigger);
    }

    if (index < activeIndex) return 1;
    if (index > activeIndex) return 0;

    return clamp(localProgress[index] || 0, 0, 1);
  }

  function getStoryRevealVisualProgress(
    sceneData,
    entry,
    index,
    activeIndex,
    localProgress,
    progress,
    firstCardTrigger,
    rect
  ) {
    const stickyProgress = getCardTimelineProgress(
      sceneData,
      entry,
      index,
      activeIndex,
      localProgress,
      progress,
      firstCardTrigger
    );

    if (!entry.storyReveal) return stickyProgress;
    if (index !== 0) return stickyProgress;

    const isLeadStoryReveal =
      entry.item &&
      entry.item.type === 'story-reveal';

    if (isLeadStoryReveal) {
      return stickyProgress;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

    const startLine = viewportHeight * SETTINGS.storyRevealPreEntryStartViewport;
    const endLine =
      sceneData.topOffset +
      viewportHeight *
        (isMobile
          ? SETTINGS.storyRevealPreEntryEndViewportMobile
          : SETTINGS.storyRevealPreEntryEndViewportDesktop);

    const preEntryRaw = mapInverseLinear(rect.top, startLine, endLine);
    const preEntryProgress = smoothstep(0, 1, preEntryRaw);

    const carry = isMobile
      ? SETTINGS.storyRevealPreEntryCarryMobile
      : SETTINGS.storyRevealPreEntryCarryDesktop;

    const carryWeight = isMobile
      ? SETTINGS.storyRevealVisualCarryWeightMobile
      : SETTINGS.storyRevealVisualCarryWeightDesktop;

    const preEntryCap = clamp(carry * carryWeight, 0, 0.84);
    const preEntryVisual = preEntryProgress * preEntryCap;

    const bridgeRaw = mapInverseLinear(rect.top, endLine, sceneData.topOffset);
    const bridgeProgress = smoothstep(0, 1, bridgeRaw);

    const bridgeLift = isMobile ? 0.16 : 0.2;
    const bridgeTarget = clamp(preEntryCap + bridgeLift, preEntryCap, 0.9);

    const leadVisual =
      preEntryVisual + (bridgeTarget - preEntryVisual) * bridgeProgress;

    return clamp(
      leadVisual + (1 - leadVisual) * stickyProgress,
      0,
      1
    );
  }

  function getActiveSwitchThreshold(sceneData, index) {
    if (index <= 0) return SETTINGS.activeSwitchThreshold;

    const previousEntry = sceneData.cards[index - 1];

    if (previousEntry && previousEntry.storyReveal) {
      return SETTINGS.storyRevealNextCardSwitchThreshold;
    }

    return SETTINGS.activeSwitchThreshold;
  }

  function updateStoryRevealCard(entry, visualProgress, contentProgress, sceneRect, sceneData, sceneProgress) {
    if (!entry.storyReveal) return;

    const visual = clamp(visualProgress, 0, 1);
    const content = clamp(contentProgress, 0, 1);

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const isMobile = viewportWidth <= MOBILE_BREAKPOINT;
    const isTablet = viewportWidth > MOBILE_BREAKPOINT && viewportWidth < 990;

    const isLeadStoryReveal =
      sceneData &&
      sceneData.cards &&
      sceneData.cards[0] === entry &&
      entry.item &&
      entry.item.type === 'story-reveal';

    const isLeadStoryRevealMobile = isLeadStoryReveal && isMobile;

    const colorIntroStart = isMobile ? 0.15 : isTablet ? 0.14 : 0.13;
    const colorIntroEnd = isMobile ? 0.88 : isTablet ? 0.86 : 0.84;
    const colorIntroRange = Math.max(colorIntroEnd - colorIntroStart, 0.0001);

    const revealIntroStart = clamp(
      colorIntroStart + colorIntroRange * (isMobile ? 0.18 : isTablet ? 0.2 : 0.22),
      0,
      1
    );
    const revealIntroEnd = clamp(
      isMobile ? 0.985 : isTablet ? 0.975 : 0.965,
      0,
      1
    );
    const revealIntroRange = Math.max(revealIntroEnd - revealIntroStart, 0.0001);

    let dateStart = revealIntroStart;
    let dateEnd = clamp(revealIntroStart + revealIntroRange * 0.46, 0, 1);

    let titleStart = clamp(revealIntroStart + revealIntroRange * 0.30, 0, 1);
    let titleEnd = clamp(revealIntroStart + revealIntroRange * 0.72, 0, 1);

    let copyStart = clamp(revealIntroStart + revealIntroRange * 0.44, 0, 1);
    let copyEnd = clamp(revealIntroStart + revealIntroRange * 0.92, 0, 1);

    if (isLeadStoryRevealMobile) {
      const sharedRevealStart = colorIntroStart;
      const sharedRevealRange = colorIntroRange;

      dateStart = sharedRevealStart;
      dateEnd = clamp(sharedRevealStart + sharedRevealRange * 0.28, 0, 1);

      titleStart = clamp(sharedRevealStart + sharedRevealRange * 0.18, 0, 1);
      titleEnd = clamp(sharedRevealStart + sharedRevealRange * 0.48, 0, 1);

      copyStart = clamp(sharedRevealStart + sharedRevealRange * 0.56, 0, 1);
      copyEnd = clamp(sharedRevealStart + sharedRevealRange * 0.78, 0, 1);
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    let colorDriver = visual;

    if (isLeadStoryReveal) {
      const colorPreStartLine = viewportHeight * (isMobile ? 0.91 : isTablet ? 0.95 : 0.99);
      const colorPreEndLine =
        sceneData.topOffset +
        viewportHeight * (isMobile ? 0.31 : isTablet ? 0.35 : 0.39);

      const colorPreRaw = mapInverseLinear(sceneRect.top, colorPreStartLine, colorPreEndLine);
      const colorPreProgress = smoothstep(0, 1.1, colorPreRaw);

      colorDriver = Math.max(visual, colorPreProgress);
    }

    const revealDriver = isLeadStoryRevealMobile ? colorDriver : visual;

    const dateReveal = smoothstep(dateStart, dateEnd, revealDriver);
    const titleReveal = smoothstep(titleStart, titleEnd, revealDriver);
    const copyReveal = smoothstep(copyStart, copyEnd, revealDriver);

    const backgroundStart = colorIntroStart;
    const backgroundEnd = 1;
    const backgroundPhase = smoothstep(backgroundStart, backgroundEnd, colorDriver);

    const mediaReveal = 1;
    const mediaYOffset = 0;
    const mediaScale = 1;
    const mediaDarkness = 0;

    let shiftDriver = content;

    if (isLeadStoryReveal) {
      const nextSegment = Math.max(sceneData.segments[1] || sceneData.leadDistance || 1, 1);

      shiftDriver = clamp(
        sceneProgress / nextSegment,
        0,
        1
      );
    }

    const shiftStart = isLeadStoryReveal ? 0 : SETTINGS.storyRevealScrollStart;
    const shiftEnd = isLeadStoryReveal ? 1 : SETTINGS.storyRevealScrollEnd;

    const shiftPhase = smoothstep(
      shiftStart,
      shiftEnd,
      shiftDriver
    );

    const shift = -Math.round(shiftPhase * (entry.storyReveal.overflow || 0));

    const mixedBackground = mixColor(
      entry.storyReveal.baseColor,
      entry.storyReveal.targetColor,
      backgroundPhase
    );

    entry.storyReveal.root.style.backgroundColor = toRgbString(mixedBackground);
    entry.storyReveal.root.style.setProperty('--ct-date-reveal', dateReveal.toFixed(4));
    entry.storyReveal.root.style.setProperty('--ct-title-reveal', titleReveal.toFixed(4));
    entry.storyReveal.root.style.setProperty('--ct-copy-reveal', copyReveal.toFixed(4));
    entry.storyReveal.root.style.setProperty('--ct-media-opacity', mediaReveal.toFixed(4));
    entry.storyReveal.root.style.setProperty('--ct-media-y', `${mediaYOffset.toFixed(2)}px`);
    entry.storyReveal.root.style.setProperty('--ct-media-scale', mediaScale.toFixed(4));
    entry.storyReveal.root.style.setProperty('--ct-media-darkness', mediaDarkness.toFixed(4));
    entry.storyReveal.root.style.setProperty('--ct-story-stack-shift', `${shift}px`);
  }

  function renderScene(sceneData, suppliedRect) {
    const rect = suppliedRect || sceneData.scene.getBoundingClientRect();
    const progress = clamp(sceneData.topOffset - rect.top, 0, sceneData.scrollDistance);
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const sceneVisible = rect.bottom > sceneData.topOffset + 40;

    const firstCardTrigger = rect.top <= viewportHeight * 0.8 && sceneVisible;
    const laterCardsTrigger = rect.top <= viewportHeight * 0.8 && sceneVisible;

    let activeIndex = 0;
    const localProgress = [];
    const baseYPositions = [];

    sceneData.cards.forEach((entry, index) => {
      let y = 0;
      let raw = 0;
      let local = 0;

      entry.dualShiftProgress = 0;

      if (index > 0) {
        const start = sceneData.starts[index] || 0;
        const segment = sceneData.segments[index] || 1;
        const distance = progress - start;

        raw = clamp(distance / segment, 0, 1);

        const incomingTravel = getIncomingTravelDistance(
          sceneData.cards,
          index,
          sceneData.stageHeight,
          sceneData.topOffset
        );

        const baseSegment = Math.max(
          incomingTravel - getEntryPeek(entry, isMobile),
          120
        );

        const extraDistance = Math.max(segment - baseSegment, 0);

        const hasMobileDualReveal =
          entry.dualImageReveal &&
          entry.dualImageReveal.isStackedLayout;

        let motionRaw = raw;

        if (hasMobileDualReveal && extraDistance > 0) {
          const overlapDistance = Math.round(
            baseSegment * clamp(SETTINGS.dualImageRevealMobileOverlap, 0, 0.35)
          );

          const shiftStart = Math.max(baseSegment - overlapDistance, 1);
          const shiftDistance = Math.max(extraDistance + overlapDistance, 1);

          motionRaw = clamp(distance / baseSegment, 0, 1);

          entry.dualShiftProgress = clamp(
            (distance - shiftStart) / shiftDistance,
            0,
            1
          );
        }

        const baseLocal = getEntryProgress(motionRaw);

        const hasLeadStoryRevealFollowCard =
          sceneData.cards[0] &&
          sceneData.cards[0].item &&
          sceneData.cards[0].item.type === 'story-reveal' &&
          index === 1;

        let entryProgress = baseLocal;
        let entryTravel = incomingTravel;

        if (hasLeadStoryRevealFollowCard) {
          const followCardEntryOffset = isMobile ? 120 : 180;
          const entryBleed = isMobile ? 0.08 : 0.14;
          const bledSourceRaw = hasMobileDualReveal ? motionRaw : raw;
          const bledRaw = clamp((bledSourceRaw + entryBleed) / (1 + entryBleed), 0, 1);
          const easedProgress = smoothstep(0, 1, bledRaw);

          entryProgress = easedProgress;
          entryTravel = sceneData.stageHeight + followCardEntryOffset;
        }

        local = entryProgress;
        y = Math.round((1 - entryProgress) * entryTravel);

        if (local > getActiveSwitchThreshold(sceneData, index)) {
          activeIndex = index;
        }
      }

      localProgress[index] = local;
      baseYPositions[index] = y;
    });

    sceneData.cards.forEach((entry, index) => {
      const isActive = index === activeIndex;
      const isBehind = index < activeIndex;
      const isUpcoming = index > activeIndex;

      let shouldRevealNow = false;

      if (index === 0) {
        shouldRevealNow = isActive && firstCardTrigger;
      } else {
        shouldRevealNow = isActive && laterCardsTrigger && localProgress[index] > 0.45;
      }

      if (shouldRevealNow) {
        queueCardReveal(entry);
      }

      const contentProgress = getCardTimelineProgress(
        sceneData,
        entry,
        index,
        activeIndex,
        localProgress,
        progress,
        firstCardTrigger
      );

      const visualProgress = getStoryRevealVisualProgress(
        sceneData,
        entry,
        index,
        activeIndex,
        localProgress,
        progress,
        firstCardTrigger,
        rect
      );

      updateStoryRevealCard(entry, visualProgress, contentProgress, rect, sceneData, progress);

      const dualShiftProgress =
        index === 0 &&
        entry.dualImageReveal &&
        entry.dualImageReveal.isStackedLayout &&
        sceneData.leadDistance > 0
          ? clamp(progress / sceneData.leadDistance, 0, 1)
          : entry.dualShiftProgress || 0;

      updateDualImageRevealCard(entry, dualShiftProgress);

      const behindState = getBehindState(
        index,
        activeIndex,
        localProgress,
        sceneData,
        isMobile
      );

      const finalY = baseYPositions[index] - behindState.drift;

      entry.card.style.setProperty('--ct-card-y', `${finalY}px`);
      entry.card.style.setProperty('--ct-card-overlay-opacity', `${behindState.overlay.toFixed(3)}`);

      entry.card.classList.toggle('is-active', isActive);
      entry.card.classList.toggle('is-behind', isBehind);
      entry.card.classList.toggle('is-upcoming', isUpcoming);
      entry.card.classList.toggle('is-revealed', entry.hasRevealed);
    });
  }

  function getGroupKey(group) {
    return group
      .map((item) => item.sectionId || (item.section && item.section.id) || '')
      .join('|');
  }

  function isGroupNearViewport(group, topOffset) {
    if (!group || !group.length) return false;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const firstRect = group[0].section.getBoundingClientRect();
    const lastRect = group[group.length - 1].section.getBoundingClientRect();

    const topBuffer = viewportHeight * 1.5;
    const bottomBuffer = viewportHeight * 2.5;

    return (
      lastRect.bottom > topOffset - topBuffer &&
      firstRect.top < viewportHeight + bottomBuffer
    );
  }

  function buildVisibleScenes() {
    if (!pendingGroups.length) return false;

    const topOffset = getHeaderOffset();
    const remainingGroups = [];
    let builtAny = false;

    pendingGroups.forEach((group) => {
      const groupKey = getGroupKey(group);

      if (builtGroupKeys.has(groupKey)) {
        return;
      }

      if (!isGroupNearViewport(group, topOffset)) {
        remainingGroups.push(group);
        return;
      }

      const sceneData = createScene(group, topOffset);

      builtGroupKeys.add(groupKey);

      if (sceneData) {
        scenes.push(sceneData);
        builtAny = true;
      }
    });

    pendingGroups = remainingGroups;

    return builtAny;
  }

  function isSceneNearViewport(sceneData, rect) {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const topBuffer = viewportHeight * 1.25;
    const bottomBuffer = viewportHeight * 1.75;

    return (
      rect.bottom > sceneData.topOffset - topBuffer &&
      rect.top < viewportHeight + bottomBuffer
    );
  }

  function renderAllScenes() {
    scenes.forEach((sceneData) => {
      const rect = sceneData.scene.getBoundingClientRect();

      if (!isSceneNearViewport(sceneData, rect)) {
        return;
      }

      renderScene(sceneData, rect);
    });
  }

  function scheduleRender() {
    if (renderFrame) return;

    renderFrame = window.requestAnimationFrame(() => {
      renderFrame = null;
      buildVisibleScenes();
      renderAllScenes();
    });
  }

  function buildAllScenes() {
    debugRebuildCount += 1;

    debugStack('buildAllScenes:start', {
      rebuildCount: debugRebuildCount,
      currentScenes: document.querySelectorAll('.ct-scroll-scene').length
    });

    if (renderFrame) {
      window.cancelAnimationFrame(renderFrame);
      renderFrame = null;
    }

    clearScenes();

    const items = getEligibleSources();
    pendingGroups = buildGroups(items);

    debugStack('buildAllScenes:groups', {
      itemsCount: items.length,
      groups: pendingGroups.map((group) => {
        return group.map((item) => `${item.type}:${item.sectionId}`).join(' > ');
      })
    });

    buildVisibleScenes();
    renderAllScenes();

    document.documentElement.classList.add('ct-scroll-scenes-ready');

    debugStack('buildAllScenes:end', {
      scenesCountAfterBuild: scenes.length,
      pendingGroupsAfterBuild: pendingGroups.length
    });
  }

  function getViewportWidth() {
    return window.innerWidth || document.documentElement.clientWidth;
  }

  function handleResize() {
    const currentViewportWidth = getViewportWidth();
    const currentViewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const widthDifference = Math.abs(currentViewportWidth - lastViewportWidth);
    const heightDifference = Math.abs(currentViewportHeight - lastViewportHeight);

    const previousViewportMode = lastViewportWidth <= MOBILE_BREAKPOINT
      ? 'mobile'
      : lastViewportWidth < 990
        ? 'tablet'
        : 'desktop';

    const currentViewportMode = currentViewportWidth <= MOBILE_BREAKPOINT
      ? 'mobile'
      : currentViewportWidth < 990
        ? 'tablet'
        : 'desktop';

    const modeChanged = previousViewportMode !== currentViewportMode;
    const isTouchLayout = previousViewportMode !== 'desktop' || currentViewportMode !== 'desktop';

    if (isTouchLayout && !modeChanged && widthDifference < 24) {
      debugStack('resize:renderOnly', {
        widthDifference,
        heightDifference,
        previousViewportMode,
        currentViewportMode,
        lastViewportWidth,
        currentViewportWidth,
        lastViewportHeight,
        currentViewportHeight
      });

      lastViewportWidth = currentViewportWidth;
      lastViewportHeight = currentViewportHeight;
      scheduleRender();
      return;
    }

    debugStack('resize:rebuild', {
      widthDifference,
      heightDifference,
      previousViewportMode,
      currentViewportMode,
      lastViewportWidth,
      currentViewportWidth,
      lastViewportHeight,
      currentViewportHeight
    });

    lastViewportWidth = currentViewportWidth;
    lastViewportHeight = currentViewportHeight;
    scheduleRebuild('resize');
  }

  function handleOrientationChange() {
    debugStack('orientationchange:start');

    window.clearTimeout(resizeTimer);

    resizeTimer = window.setTimeout(() => {
      lastViewportWidth = getViewportWidth();
      lastViewportHeight = window.innerHeight || document.documentElement.clientHeight;

      debugStack('orientationchange:run');

      buildAllScenes();
    }, 300);
  }

  function scheduleRebuild(reason = 'unknown') {
    debugStack('scheduleRebuild', {
      reason
    });

    window.clearTimeout(resizeTimer);

    resizeTimer = window.setTimeout(() => {
      debugStack('scheduleRebuild:run', {
        reason
      });

      buildAllScenes();
    }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildAllScenes, { once: true });
  } else {
    buildAllScenes();
  }

  window.addEventListener('load', () => {
    scheduleRender();
    window.setTimeout(() => scheduleRebuild('load:settled'), 350);
  }, { once: true });

  window.addEventListener('pageshow', () => {
    scheduleRebuild('pageshow');
  });
  window.addEventListener('scroll', scheduleRender, { passive: true });
  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleOrientationChange);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      window.setTimeout(() => {
        scheduleRebuild('fonts:ready');
      }, 250);
    }).catch(() => {});
  }

  document.addEventListener('shopify:section:load', () => scheduleRebuild('shopify:section:load'));
  document.addEventListener('shopify:section:unload', () => scheduleRebuild('shopify:section:unload'));
  document.addEventListener('shopify:section:reorder', () => scheduleRebuild('shopify:section:reorder'));
  document.addEventListener('shopify:block:select', () => scheduleRebuild('shopify:block:select'));
  document.addEventListener('shopify:block:deselect', () => scheduleRebuild('shopify:block:deselect'));
})();
